<script setup lang="ts">
import { AppStore as AppStoreEnum } from '@pkg/sanity-utils/constants/appstore';
import { Loader2 } from 'lucide-vue-next';
import { QrCode } from '@pkg/ui/components/ui/qr-code';
import type { PortableTextBlock } from 'sanity';
import Nudge from '~/app/components/blocks/Nudge.vue';

defineProps<{
  qrTitle: string;
  qrSubtitle: string;
  deepLink: string;
  altText?: string | 'Too Good To Go QR Code';
  nudge: {
    nudgeTitle: string;
    steps: Array<{
      icon: string;
      title: string;
      content: Array<PortableTextBlock>;
    }>;
  };
  isDesktop: boolean;
}>();

const showQrLoader = ref(true);
const handleQrLoader = (qrGenerated: boolean) => { showQrLoader.value = !qrGenerated; };

</script>

<template>
  <div v-if="isDesktop && nudge">
    <h2 class="s-qr__title__section mb-8">
      {{ nudge?.nudgeTitle }}
    </h2>
    <Nudge :steps="nudge?.steps" class="mb-16" />
  </div>
  <div class="s-qr__content">
    <QrCode class="s-qr" :url="deepLink" :alt="altText" @qr-generated="handleQrLoader" />
    <Loader2 v-if="showQrLoader" class="s-qr s-qr__loader animate-spin" />
    <h2 class="s-qr__title">
      {{ qrTitle }}
    </h2>
    <p class="s-qr__subtitle">
      {{ qrSubtitle }}
    </p>
  </div>
  <div class="s-qr__links">
    <AppStore :app-store="AppStoreEnum.APPLE_APP_STORE" />
    <AppStore :app-store="AppStoreEnum.GOOGLE_PLAY_STORE" />
    <AppStore :app-store="AppStoreEnum.HUAWEI_APP_GALLERY" />
  </div>
</template>

<style scoped>
@import '~/assets/css/mixins/_mixins.index.pcss';

.s-qr {
  display: flex;
  flex-direction: column;

  @screen tablet-wide {
    display: flex;
    width: 80%;
    height: 50%;
  }

  &__content {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: theme('spacing.11') theme('spacing.0') theme('spacing.11') theme('spacing.0');
    text-align: center;

    @screen tablet-wide {
      border: 1px solid rgba(37, 45, 45, 0.075);
      border-radius: 8px;
      box-shadow: 0px 2px 8px 2px rgba(37, 45, 45, 0.075);
      padding: theme('spacing.11') theme('spacing.0') theme('spacing.56') theme('spacing.0');
    }
  }

  &__links {
    display: flex;
    flex-direction: column;
    max-width: 100%;
    flex-wrap: no-wrap;
    justify-content: space-between;
    gap: theme('spacing.8');
    margin-top: theme('margin.24');
    align-items: center;

    :deep(img) {
      max-width: 100%;
    }

    @screen desk {
      flex-direction: row;
    }
  }

  &__loader {
    height: theme('spacing.32');
    width: theme('spacing.32');
    color:var(--theme-heading-color-app);
    margin: theme('spacing.56');

    @screen desk {
      margin: theme('spacing.96');
    }
  }

  &__title {
    @mixin h5-tenon;
    font-weight: bold;
    color: var(--theme-heading-color-app);
    padding: theme('spacing.20') theme('spacing.0') theme('spacing.8') theme('spacing.0');

    &__section {
      @mixin h5-tenon;
      font-weight: bold;
      padding-bottom: theme('spacing.8');
    }
  }

  &__subtitle {
    @mixin body;
    width: 80%;
  }
}
</style>
